import type React from 'react';

/**
 * OJK Compliance Disclaimer — SPA variant (soft mitigation 14 Mei 2026).
 *
 * Mirror dari `tradertekno-site/components/OJKDisclaimerBanner.tsx` untuk
 * marketing site. SPA version pakai vanilla anchor instead of next/link.
 *
 * Mount di high-exposure pages: SignalsPage, SignalDetailPage.
 *
 * Variants:
 *   - 'prominent': sticky top banner (default untuk page dengan signal data)
 *   - 'compact': inline notice
 *   - 'footer': bottom disclaimer block
 *
 * Rationale: TT current state MEDIUM-HIGH risk per POJK 13/2025. Aggressive
 * disclosure shifts ke MEDIUM tanpa change product features.
 */
export type DisclaimerVariant = 'prominent' | 'compact' | 'footer';

const PERINGATAN_RISIKO_URL = 'https://tradertekno.com/peringatan-risiko';

export const OJKDisclaimerBanner: React.FC<{ variant?: DisclaimerVariant }> = ({ variant = 'prominent' }) => {
  if (variant === 'prominent') {
    return (
      <div className="bg-amber-50 border-l-4 border-amber-500 px-4 py-3 sm:px-6 sm:py-4">
        <div className="mx-auto max-w-3xl flex items-start gap-3">
          <span aria-hidden="true" className="flex-shrink-0 text-amber-600 text-lg leading-none mt-0.5">⚠️</span>
          <div className="text-xs sm:text-sm text-amber-900 leading-relaxed">
            <strong>TraderTekno BUKAN Penasihat Investasi terdaftar OJK.</strong>{' '}
            Konten ini bersifat <strong>EDUKATIF</strong> — sinyal AI adalah indikator data berbasis pola historis,{' '}
            <strong>BUKAN rekomendasi atau prediksi membeli/menjual saham</strong>. Tidak ada jaminan akurasi.
            Pengguna <strong>WAJIB konsultasi Penasihat Investasi berlisensi OJK</strong> sebelum keputusan investasi.{' '}
            <a href={PERINGATAN_RISIKO_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-700 font-medium">
              Baca peringatan risiko lengkap →
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 text-xs text-amber-900 leading-relaxed">
        <strong>⚠️ Disclaimer:</strong> TraderTekno bukan Penasihat Investasi OJK. Konten edukatif —
        bukan rekomendasi investasi.{' '}
        <a href={PERINGATAN_RISIKO_URL} target="_blank" rel="noopener noreferrer" className="underline">Detail risiko</a>.
      </div>
    );
  }

  return (
    <div className="border-t border-slate-200 pt-6 mt-12">
      <p className="text-xs text-slate-500 leading-relaxed">
        <strong>Disclaimer Penting:</strong> TraderTekno adalah platform <strong>edukasi</strong> analisis
        saham berbasis AI dan <strong>BUKAN Penasihat Investasi terdaftar OJK</strong>. Sinyal AI yang
        ditampilkan adalah indikator data berbasis pola historis, <strong>BUKAN rekomendasi atau prediksi
        membeli/menjual saham</strong>. Tidak ada jaminan akurasi atas analisis maupun sinyal. TraderTekno
        tidak bertanggung jawab atas kerugian akibat keputusan investasi pengguna. Pengguna <strong>WAJIB
        konsultasi dengan Penasihat Investasi berlisensi OJK</strong> sebelum keputusan investasi.{' '}
        <a href={PERINGATAN_RISIKO_URL} target="_blank" rel="noopener noreferrer" className="underline">Baca peringatan risiko lengkap</a>.
      </p>
    </div>
  );
};

export default OJKDisclaimerBanner;
